/* RECAROチャイルド適合チェック エンジン（XENON 1 / AXION 1 / VELIX 1 共用）
 * 車名のフリーワード入力 → 適合データ照合 → 座席別判定。
 * データにない車・年式は製品別のISOFIX推定フローで見込みを案内する。
 * 判定はすべてルールベース（LLM等の外部APIは使用しない）。
 *
 * 使い方: [data-ai-fit] を持つコンテナごとに自動初期化する。
 *   data-product      = "xenon1" | "axion1" | "velix1"
 *   data-car-fit-url  = car_fit_*.json のURL
 *   data-symbols-url  = compatibility_symbols.json のURL
 *   data-aliases-url  = car_aliases.json のURL（省略可）
 * プレビュー埋め込み用に window.AI_FIT_EMBEDDED があれば fetch せずそれを使う。
 */
(function () {
  "use strict";

  const LINE_URL = "https://s.lmes.jp/landing-qr/2007214163-O9b0xb0E?uLand=5GBBhJ";
  const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- 計測フック（GTM/GA4があれば送る。無ければ何もしない） ----------
     dataLayer を優先する: このサイトでは Shopify が gtag 関数をグローバル定義しているため、
     gtag 優先にすると GTM（GTM-NPRJXLNX のカスタムイベントトリガー）を迂回してしまう */
  function track(eventName, params) {
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: eventName }, params));
      } else if (typeof window.gtag === "function") {
        window.gtag("event", eventName, params);
      }
    } catch (e) {
      /* 計測失敗は無視する */
    }
  }

  /* ---------- 汎用ユーティリティ ---------- */
  const sleep = (ms) => new Promise((r) => setTimeout(r, REDUCED ? 0 : ms));

  function norm(s) {
    return s
      .normalize("NFKC")
      .replace(/[ぁ-ゖ]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 0x60))
      .toUpperCase()
      .replace(/[\s・･\-‐－―‑–—/／\\()（）［\]\[】【※、。,.]/g, "");
  }

  function lev(a, b) {
    if (Math.abs(a.length - b.length) > 2) return 99;
    const dp = Array.from({ length: a.length + 1 }, (_, i) => [i]);
    for (let j = 1; j <= b.length; j++) dp[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
        );
      }
    }
    return dp[a.length][b.length];
  }

  /* 日本市場で一般的な表記に合わせたメーカー表示名。
     データのカタカナ読み（ビーエムダブリュ等）や、読みが入っていないラテン表記（FERRARI等）を
     そのまま出すと日本語ページで浮くため、明示マップで上書きする。
     ⚠️ 前方から順に部分一致で判定するため、より長いメーカー名を先に置くこと
     （"ROVER" を先に置くと "LAND ROVER" まで「ローバー」になる）。 */
  const BRAND_DISPLAY = [
    { token: "BMW", label: "BMW／MINI" },
    { token: "NISSAN", label: "日産" },
    { token: "MERCEDES", label: "メルセデス・ベンツ" },
    { token: "CHRYSLER", label: "クライスラー／ジープ" },
    { token: "CITROEN", label: "シトロエン／DS" },
    { token: "MASERATI", label: "マセラティ" },
    { token: "ISUZU", label: "いすゞ" },
    { token: "LAND ROVER", label: "ランドローバー" },
    { token: "ROVER", label: "ローバー" },
    { token: "ASTON", label: "アストンマーティン" },
    { token: "BUICK", label: "ビュイック" },
    { token: "FERRARI", label: "フェラーリ" },
    { token: "FORD", label: "フォード" },
    { token: "HUMMER", label: "ハマー" },
    { token: "HYUNDAI", label: "ヒョンデ" },
    { token: "LANCIA", label: "ランチア" },
    { token: "LINCOLN", label: "リンカーン" },
    { token: "OPEL", label: "オペル" },
    { token: "SAAB", label: "サーブ" },
    { token: "SMART", label: "スマート" },
    { token: "TESLA", label: "テスラ" },
  ];

  function brandShort(brand) {
    for (const b of BRAND_DISPLAY) {
      if (brand.includes(b.token)) return b.label;
    }
    const m = brand.match(/[（(]([^）)]+)[）)]/);
    return m ? m[1] : brand.split(/[／(（]/)[0].trim();
  }

  function brandChipLabel(brand) {
    for (const b of BRAND_CHIP_LABEL) {
      if (brand.includes(b.token)) return b.label;
    }
    return brandShort(brand);
  }

  function parseRange(year) {
    const m = year.match(/^(\d{4,5})(?:\.(\d{1,2}))?～(?:(\d{4,5})(?:\.(\d{1,2}))?)?$/);
    if (!m) return null;
    const s = parseInt(m[1], 10) + (m[2] ? (parseInt(m[2], 10) - 1) / 12 : 0);
    let e = Infinity;
    if (m[3]) {
      const ey = parseInt(m[3], 10);
      e = ey > 2100 ? Infinity : ey + (m[4] ? parseInt(m[4], 10) / 12 : 1);
    }
    return { s, e };
  }

  function esc(s) {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  function extractYear(text) {
    const m = text.match(/((?:19|20)\d{2})\s*年?\s*式?/);
    return m ? parseInt(m[1], 10) : null;
  }

  /* ---------- 製品別の判定・文言定義 ---------- */

  /* XENON 1: 前向き・後向きの両方向を合算判定（compatibility-fit-xenon1.js と同一基準） */
  function xenonDirDisplay(symbol) {
    const ok = symbol.id <= 10 || symbol.id === 24;
    const cond = [14, 15, 16, 19].includes(symbol.id);
    return {
      rank: ok ? 0 : cond ? 1 : 2,
      cls: ok ? "ok" : cond ? "warn" : "ng",
      icon: ok ? "✓" : cond ? "△" : "×",
      description: ok ? "取り付け可能です。" : symbol.description,
      countsAsOk: ok || cond,
    };
  }

  function xenonSeatDisplay(compat, symbolsById) {
    const front = symbolsById[compat.front.compatibility_symbol_id];
    if (front.id === 11) {
      return { cls: "ng", icon: "×", description: front.description, countsAsOk: false };
    }
    if (compat.back) {
      const f = xenonDirDisplay(front);
      const b = xenonDirDisplay(symbolsById[compat.back.compatibility_symbol_id]);
      const worse = f.rank >= b.rank ? f : b;
      let description = worse.description;
      if (worse.rank === 1) {
        if (f.rank === 0) description = "後ろ向きでの取り付けができない可能性がございます。";
        else if (b.rank === 0) description = "前向きでの取り付けができない可能性がございます。";
      }
      return { cls: worse.cls, icon: worse.icon, description, countsAsOk: worse.countsAsOk };
    }
    const d = xenonDirDisplay(front);
    return { cls: d.cls, icon: d.icon, description: d.description, countsAsOk: d.countsAsOk };
  }

  /* ×の説明文。id17（補助席・特殊座席）の2文目以降はキセノンのサポートレッグの話なので、
     サポートレッグのないアクシオン・ベリックスでは1文目だけを出す */
  function ngDescription(symbol) {
    return symbol.id === 17 ? symbol.description.split("\n")[0] : symbol.description;
  }

  /* AXION 1: 前向き専用（2026-06 事業判断、compatibility-fit-axion1.js と同一基準）。
     物理的に取付不可・調査対象外のみNG、それ以外は3点式シートベルトで取付可。
     2点式ベルト・補助席の席はデータ側で id13・id17 にしている（取扱説明書が2点式を禁止。
     docs/compatibility-judgments.md #3）。 */
  function axionSeatDisplay(compat, symbolsById) {
    const symbol = symbolsById[compat.front.compatibility_symbol_id];
    const isNg = [11, 12, 13, 17, 22, 23].includes(symbol.id);
    const isIsofixOk = symbol.id <= 10;
    let description;
    if (isIsofixOk) {
      description = symbol.description;
    } else if (symbol.id === 20) {
      description = "ISOFIX取付金具が装備されていませんが、お車の3点式シートベルトを使用して取り付けることができます。";
    } else if (!isNg) {
      description = "お車の3点式シートベルトを使用して取り付けることができます。※お車の形状等により取り付けにくい場合は、商品到着後7日以内であれば返品を承ります。";
    } else {
      description = ngDescription(symbol);
    }
    return { cls: isNg ? "ng" : "ok", icon: isNg ? "×" : "✓", description, countsAsOk: !isNg };
  }

  /* VELIX 1: 前向き専用。取扱説明書（第4・5面）どおり、ISOFIX は i-Size 対応座席だけで使い、
     3点式ベルトで付けるのは i-Size 対応座席のないお車での 100〜150cm（ジュニアモード）だけ。
     3点式ベルトで付けるジュニアモードはユニバーサルブースターなので、お車の取扱説明書で汎用のチャイルドシートを
     付けられない席は×（id33）。データは docs/build-velix1-fit.py で作る。id25〜33 はベリックス専用
     （根拠は docs/compatibility-judgments.md #2） */
  const VELIX_OK = [25];
  const VELIX_NG = [11, 12, 13, 17, 22, 23, 31, 33];
  const VELIX_RETURN_NOTE = "※お車の座席の形やシートベルトの位置により取り付けにくい場合は、商品到着後7日以内であれば返品を承ります。";
  const VELIX_CHECK_NOTE = "※トップテザー金具・ISOFIX金具の有無や位置、座席の形によっては、76〜105cm（5点式ハーネス）で取り付けできない場合があります。お車の取扱説明書でご確認ください。";
  const VELIX_UNIVERSAL_NOTE = "※お車の取扱説明書で、純正・指定のチャイルドシートに限られている座席です。取り付けの前に、お車の取扱説明書でご確認ください。";

  function isizeFromLabel(value) {
    const m = /^(\d{4})\.(\d{1,2})$/.exec(value || "");
    return m ? `${m[1]}年${parseInt(m[2], 10)}月` : "一部の年式";
  }

  function velixSeatDisplay(compat, symbolsById, entry) {
    const symbol = symbolsById[compat.front.compatibility_symbol_id];
    const isOk = VELIX_OK.includes(symbol.id);
    const isNg = VELIX_NG.includes(symbol.id);
    const from = isizeFromLabel(entry && entry.isize_from);
    const base = isNg ? ngDescription(symbol) : symbol.description.split("{from}").join(from);
    const description = base
      + (compat.front.check_note ? VELIX_CHECK_NOTE : "")
      + (compat.front.universal_note ? VELIX_UNIVERSAL_NOTE : "")
      + (compat.front.return_note ? VELIX_RETURN_NOTE : "");
    return {
      cls: isOk ? "ok" : isNg ? "ng" : "warn",
      icon: isOk ? "✓" : isNg ? "×" : "△",
      description,
      countsAsOk: !isNg,
    };
  }

  /* ベリックスの判定見出し。ジュニアモード（100〜150cm）しか使えない車で「取り付け可能」とだけ出すと、
     身長100cm未満のお子さまのご家族が誤解するため、使える範囲で見出しを分ける */
  function velixVerdict(symbolIds) {
    const has = (ids) => symbolIds.some((id) => ids.includes(id));
    if (has([25])) return { cls: "ok", text: "76cmから取り付けできる座席があります" };
    if (has([26])) return { cls: "cond", text: "i-Size対応座席があります（金具の位置・座席の形の確認が必要です）" };
    if (has([27, 29, 32])) return { cls: "cond", text: "お車の年式・仕様により、使える範囲が変わります" };
    if (has([28, 30])) return { cls: "cond", text: "100〜150cm（ジュニアモード）で取り付けできる座席があります" };
    return { cls: "ng", text: "この車両には取り付けできません" };
  }

  function defaultVerdict(symbolIds, anyOk) {
    return anyOk
      ? { cls: "ok", text: "取り付け可能な座席があります" }
      : { cls: "ng", text: "この車両には取り付けできません" };
  }

  /* 前の世代までしか調査データがない新型での案内（キセノン・アクシオン共通） */
  const ISOFIX_PREV_MODEL = {
    message: (carLabel, prev, year) => `<p><b>${esc(carLabel)}</b> は、ひとつ前のモデル（年式 ${esc(prev.year)}・型式 ${esc(prev.type)}）まで適合調査が完了しており、取り付けできることを確認しています。</p><p>${year}年式の新しいモデルはまだ調査データに掲載されていませんが、ISOFIX対応のお車のため、<b>同様に適合する可能性が高い</b>と考えられます。念のため、取り付け条件をご一緒に確認します。</p>`,
    lead: (prevYear) => `ひとつ前のモデル（年式 ${prevYear}）までは適合調査で取り付けを確認済みです。未調査の最新モデルも、`,
  };

  const PRODUCTS = {
    xenon1: {
      display: "XENON 1",
      label: "キセノン 1",
      seatDisplay: xenonSeatDisplay,
      resultNote:
        "※ 回転機能に関して: キャプテンシート（独立シート）のお車では、肘掛けやセンターコンソールとの干渉により回転機能を十分にご使用いただけない場合がございます。回転機能を活用されたい場合は、実店舗にて取付テストを行うか、事前に弊社宛にお問い合わせください。",
      fallback: "xenon",
      /* 「商品購入ページへ」の行き先。適合を確かめた人を、その製品の購入ページへ直接送る */
      buyUrl: "/products/xenon1",
      verdict: defaultVerdict,
      dataSourceLabel: "調査データ確認済",
      prevModel: ISOFIX_PREV_MODEL,
    },
    axion1: {
      display: "AXION 1",
      label: "アクシオン 1",
      seatDisplay: axionSeatDisplay,
      resultNote:
        "※ アクシオン1は前向き専用のジュニアシートです（対象身長 約100〜150cm／3〜12歳頃）。どのお車でも後向きでの取り付けはできません。",
      fallback: "axion",
      buyUrl: "/products/axion1",
      verdict: defaultVerdict,
      dataSourceLabel: "調査データ確認済",
      prevModel: ISOFIX_PREV_MODEL,
    },
    velix1: {
      display: "VELIX 1",
      label: "ベリックス 1",
      seatDisplay: velixSeatDisplay,
      resultNote:
        "※ ベリックス1は前向き専用です（生後15か月以上かつ身長76cm以上から）。76〜105cm（5点式ハーネス）は、i-Size対応座席にISOFIXとトップテザーで取り付けます。i-Size対応座席かどうかは、お車の取扱説明書でもご確認ください。",
      fallback: "velix",
      buyUrl: "/products/velix1",
      verdict: velixVerdict,
      /* ベリックス自体は Artsana の適合調査を受けていない。調査データの i-Size 列などから判定している */
      dataSourceLabel: "調査データから判定",
      /* 座席データが空のレコードは「取り付けできません」ではなく推定フローで案内する */
      fallbackWhenNoSeatData: true,
      prevModel: {
        message: (carLabel, prev, year) => `<p><b>${esc(carLabel)}</b> は、ひとつ前のモデル（年式 ${esc(prev.year)}・型式 ${esc(prev.type)}）にi-Size対応座席があることを、調査データで確認しています。</p><p>${year}年式の新しいモデルはまだ調査データに掲載されていませんが、<b>i-Size対応座席がある可能性が高い</b>と考えられます。念のため、取り付け条件をご一緒に確認します。</p>`,
        lead: (prevYear) => `ひとつ前のモデル（年式 ${prevYear}）にはi-Size対応座席があります。`,
      },
    },
  };

  /* ---------- メーカー選択UIの定義 ----------
     メーカーは「国産」「輸入車」の2グループで全件を提示する（一覧に隠しを作らない）。
     ここで決めるのは並び順だけで、選択肢そのものは適合データのメーカー一覧から生成する。 */

  /* 国産メーカーの表示順。値はデータのメーカー文字列に含まれる部分文字列 */
  const JP_BRAND_ORDER = ["トヨタ", "ホンダ", "ニッサン", "三菱", "マツダ", "スバル", "スズキ", "ダイハツ", "ISUZU"];

  /* 輸入車グループの先頭に置くメーカー（問い合わせが多い順）。残りは表示名の五十音順 */
  const IMPORT_BRAND_HEAD = ["BMW", "MERCEDES", "AUDI", "VOLKS", "VOLVO"];

  /* メーカーを選ぶ場面（チップと直後の確認文）だけで使う表示名。
     1つのデータ行に2ブランドが同居する場合に併記して探しやすくする。
     判定結果の車両表示は brandShort のまま（「トヨタ アルファード」を併記で冗長にしないため） */
  const BRAND_CHIP_LABEL = [{ token: "TOYOTA", label: "トヨタ／レクサス" }];

  /* テキスト入力されたメーカー名の解釈用エイリアス。
     値はデータのメーカー文字列（例: "MERCEDES BENZ(メルセデスベンツ）"）に含まれる文字列。
     カッコ内の読みやラテン表記で拾えるもの（トヨタ・BMW等）は登録不要 */
  const BRAND_TEXT_ALIASES = {
    "ベンツ": "MERCEDES",
    "メルセデス": "MERCEDES",
    "日産": "ニッサン",
    "ビーエム": "BMW",
    "ビーエムダブリュー": "BMW",
    "ワーゲン": "VOLKS",
    "フォルクス": "VOLKS",
    "レンジローバー": "LAND ROVER",
    "ヒョンデ": "HYUNDAI",
    "ヒュンダイ": "HYUNDAI",
    "いすゞ": "ISUZU",
    "イスズ": "ISUZU",
    "テスラ": "TESLA",
    "フェラーリ": "FERRARI",
    "フォード": "FORD",
    "オペル": "OPEL",
    "サーブ": "SAAB",
    "スマート": "SMART",
    "ランチア": "LANCIA",
    "リンカーン": "LINCOLN",
    "ビュイック": "BUICK",
    "ハマー": "HUMMER",
    "アストンマーティン": "ASTON",
    "アストンマーチン": "ASTON",
  };

  /* よく調べられる車種の候補（実データに存在することを検証済み） */
  const POPULAR = {
    "トヨタ": ["アルファード", "ヴォクシー", "ノア", "シエンタ", "ハリアー", "プリウス", "ヤリスクロス", "ルーミー"],
    "ホンダ": ["N BOX", "フリード", "ステップワゴン", "ヴェゼル", "フィット"],
    "ニッサン": ["セレナ", "ノート", "ルークス", "エクストレイル", "サクラ"],
    "スズキ": ["スペーシア", "ハスラー", "ジムニー", "ソリオ", "ワゴンR"],
    "ダイハツ": ["タント", "ムーヴキャンバス", "ロッキー", "ミライース", "トール"],
    "三菱": ["デリカ D:5", "アウトランダー", "eKワゴン", "RVR"],
    "マツダ": ["CX-5", "CX-8", "CX-30", "MAZDA3"],
    "スバル": ["フォレスター", "レヴォーグ", "インプレッサ", "クロストレック"],
    "BMW": ["3シリーズ", "1シリーズ", "X1", "X3", "5シリーズ", "MINI"],
    "MERCEDES": ["Cクラス", "Aクラス", "Eクラス", "GLA", "GLC", "Bクラス"],
    "AUDI": ["A3", "A4", "Q3", "Q5", "A1"],
    "VOLKS": ["ゴルフ", "ポロ", "ティグアン", "パサート"],
    "VOLVO": ["XC60", "XC40", "V60", "V40", "XC90"],
  };

  /* ---------- ウィジェット本体（コンテナごとに独立した状態を持つ） ---------- */
  function createWidget(container) {
    const productKey = container.dataset.product || "xenon1";
    const product = PRODUCTS[productKey];
    const logEl = container.querySelector("[data-ai-fit-log]");
    if (!product || !logEl) return;

    let carData = [];
    const symbolsById = {};
    let aliasPairs = [];
    let brandList = [];
    let runId = 0;
    /* 初期表示では自動スクロールせず、ユーザーが操作を始めてから追従する */
    let autoScroll = false;

    const resetBtn = container.querySelector("[data-ai-fit-reset]");
    if (resetBtn) {
      resetBtn.addEventListener("click", () => {
        start();
        const widget = container.querySelector(".ai-fit-widget");
        if (widget) widget.scrollIntoView({ block: "start", behavior: REDUCED ? "auto" : "smooth" });
      });
    }

    /* ----- ログ描画 ----- */
    function scrollBottom(el) {
      if (!autoScroll) return;
      const target = el || logEl.lastElementChild;
      if (target) target.scrollIntoView({ block: "nearest", behavior: REDUCED ? "auto" : "smooth" });
    }

    function addBot(html) {
      const w = document.createElement("div");
      w.className = "ai-fit-bot";
      w.innerHTML = `<div class="ai-fit-avatar">R</div><div class="ai-fit-card">${html}</div>`;
      logEl.appendChild(w);
      scrollBottom(w);
      return w.querySelector(".ai-fit-card");
    }

    function addUser(text) {
      const u = document.createElement("div");
      u.className = "ai-fit-user";
      u.textContent = text;
      logEl.appendChild(u);
      scrollBottom(u);
    }

    async function addScan(title, steps) {
      const rid = runId;
      const card = addBot(`<p>${title}</p><div class="ai-fit-scan"></div>`);
      const box = card.querySelector(".ai-fit-scan");
      for (const s of steps) {
        const row = document.createElement("div");
        row.className = "ai-fit-scan-step run on";
        row.innerHTML = `<span class="ai-fit-scan-ico"><span class="ai-fit-scan-dot"></span><span class="ai-fit-scan-check">✓</span></span><span>${s}</span>`;
        box.appendChild(row);
        scrollBottom();
        await sleep(420 + Math.floor(s.length * 6));
        /* 「はじめからやり直す」で新しい会話が始まっていたら、この走行は静かに停止する */
        if (rid !== runId) return new Promise(() => {});
        row.classList.remove("run");
        row.classList.add("done");
      }
      await sleep(150);
      if (rid !== runId) return new Promise(() => {});
      return card;
    }

    function addChips(options, opts) {
      const ghostLabels = (opts && opts.ghostLabels) || [];
      return new Promise((resolve) => {
        const wrap = document.createElement("div");
        wrap.className = "ai-fit-chips";
        options.forEach((o) => {
          const b = document.createElement("button");
          b.type = "button";
          b.className = "ai-fit-chip" + (ghostLabels.includes(o.label) ? " ghost" : "");
          b.innerHTML = esc(o.label) + (o.sub ? `<small>${esc(o.sub)}</small>` : "");
          b.addEventListener("click", () => {
            autoScroll = true;
            wrap.classList.add("locked");
            b.classList.add("picked");
            resolve(o);
          });
          wrap.appendChild(b);
        });
        logEl.appendChild(wrap);
        scrollBottom(wrap);
      });
    }

    function addInput(placeholder, buttonLabel) {
      return new Promise((resolve) => {
        const wrap = document.createElement("div");
        wrap.className = "ai-fit-input-row";
        wrap.innerHTML = `<input class="ai-fit-input" type="text" placeholder="${esc(placeholder)}" aria-label="${esc(placeholder)}"><button type="button" class="ai-fit-send">${esc(buttonLabel)}</button>`;
        const input = wrap.querySelector("input");
        const btn = wrap.querySelector("button");
        const fire = () => {
          const v = input.value.trim();
          if (!v) return;
          autoScroll = true;
          wrap.classList.add("locked");
          resolve(v);
        };
        btn.addEventListener("click", fire);
        input.addEventListener("keydown", (e) => {
          if (e.key === "Enter") fire();
        });
        logEl.appendChild(wrap);
        scrollBottom(wrap);
      });
    }

    /* テキスト入力とチップ選択を同時に提示し、先に操作された方で解決する。
       opts.inputLabel     … 入力欄の上に出す見出し（「車名で入力」など）
       opts.chipsLabel     … チップ群全体の見出し
       opts.groups         … チップを見出し付きの複数グループに分けたいときに [{label, options}] を渡す
       opts.collapseLocked … 選択肢が多いステップ用。選び終わったらこのステップをログから畳む */
    function addInputWithChips(placeholder, buttonLabel, chipOptions, opts) {
      const o = opts || {};
      const groups = o.groups || [{ label: "", options: chipOptions }];
      return new Promise((resolve) => {
        let done = false;
        const parts = [];
        const finish = (result) => {
          if (done) return;
          done = true;
          parts.forEach((el) => {
            el.classList.add("locked");
            /* メーカー全件のように選択肢が多い場合、薄く残すと以降のログが読みにくくなるため畳む。
               何を選んだかは直後のボット発話・ユーザー発話で分かる */
            if (o.collapseLocked) el.classList.add("ai-fit-collapsed");
          });
          resolve(result);
        };
        const addLabel = (text, className) => {
          if (!text) return;
          const el = document.createElement("div");
          el.className = className;
          el.textContent = text;
          logEl.appendChild(el);
          parts.push(el);
        };

        addLabel(o.inputLabel, "ai-fit-step-label");

        const row = document.createElement("div");
        row.className = "ai-fit-input-row";
        row.innerHTML = `<input class="ai-fit-input" type="text" placeholder="${esc(placeholder)}" aria-label="${esc(o.inputLabel || placeholder)}"><button type="button" class="ai-fit-send">${esc(buttonLabel)}</button>`;
        const input = row.querySelector("input");
        const btn = row.querySelector("button");
        const fire = () => {
          const v = input.value.trim();
          if (!v) return;
          autoScroll = true;
          finish({ type: "text", value: v });
        };
        btn.addEventListener("click", fire);
        input.addEventListener("keydown", (e) => {
          if (e.key === "Enter") fire();
        });
        logEl.appendChild(row);
        parts.push(row);

        /* グループ表示のときは全体見出しをセクション扱いにし、各グループ名を小見出しにする */
        addLabel(o.chipsLabel, o.groups ? "ai-fit-step-label sep" : "ai-fit-chip-label");

        let lastWrap = null;
        groups.forEach((g) => {
          if (o.groups) addLabel(g.label, "ai-fit-chip-label");
          const wrap = document.createElement("div");
          wrap.className = "ai-fit-chips";
          g.options.forEach((c) => {
            const b = document.createElement("button");
            b.type = "button";
            b.className = "ai-fit-chip" + (c.ghost ? " ghost" : "");
            b.innerHTML = esc(c.label) + (c.sub ? `<small>${esc(c.sub)}</small>` : "");
            b.addEventListener("click", () => {
              autoScroll = true;
              b.classList.add("picked");
              finish({ type: "chip", option: c });
            });
            wrap.appendChild(b);
          });
          logEl.appendChild(wrap);
          parts.push(wrap);
          lastWrap = wrap;
        });
        if (lastWrap) scrollBottom(lastWrap);
      });
    }

    /* ----- 検索ロジック ----- */
    function expandQuery(q) {
      const out = new Set([q]);
      for (const p of aliasPairs) {
        if (!p.a) continue;
        if (q === p.a) p.exps.forEach((e) => out.add(e));
        else if (q.startsWith(p.a)) p.exps.forEach((e) => out.add(e + q.slice(p.a.length)));
        else if (q.includes(p.a)) p.exps.forEach((e) => out.add(q.replace(p.a, e)));
      }
      return [...out];
    }

    function matchModels(query) {
      const q = norm(query);
      if (!q) return [];
      const queries = expandQuery(q);
      const groups = new Map();
      for (const entry of carData) {
        const key = entry.brand + " " + entry.model;
        const best = groups.has(key) ? groups.get(key).score : 0;
        let score = 0;
        for (const variant of entry.model.split("\n")) {
          const v = norm(variant);
          if (!v) continue;
          for (const qx of queries) {
            if (v === qx) score = Math.max(score, 100);
            else if (qx.length >= 2 && v.startsWith(qx)) score = Math.max(score, 82);
            else if (qx.length >= 2 && v.includes(qx)) score = Math.max(score, 74);
            else if (v.length >= 2 && v.length * 2 >= qx.length && qx.includes(v)) score = Math.max(score, 60);
            else if (qx.length >= 3 && lev(v, qx) <= (qx.length >= 5 ? 2 : 1)) score = Math.max(score, 52);
          }
        }
        if (score > best) {
          groups.set(key, { brand: entry.brand, model: entry.model, score });
        }
      }
      return [...groups.values()].filter((g) => g.score >= 52).sort((a, b) => b.score - a.score);
    }

    function entriesFor(brand, model) {
      return carData.filter((d) => d.brand === brand && d.model === model);
    }

    function variantGroups(entries) {
      const seen = new Map();
      for (const e of entries) {
        const key = e.type + " " + e.year;
        if (!seen.has(key)) seen.set(key, { type: e.type, year: e.year, entries: [] });
        seen.get(key).entries.push(e);
      }
      return [...seen.values()];
    }

    function coveredGroups(groups, year) {
      return groups.filter((g) => {
        const r = parseRange(g.year);
        return r && year + 1 > r.s && year < r.e + 0.001;
      });
    }

    /* ----- フロー ----- */
    async function start() {
      runId++;
      autoScroll = false;
      logEl.innerHTML = "";
      addBot(`<p>RECARO ${product.display} の適合チェックを行います。<br>お車の<b>車名</b>がわかる方は、そのまま入力するのが最短です。メーカーから順にたどることもできます。</p><div class='ai-fit-hint'>例: アルファード ／ N-BOX ／ ランクル ／ 86 2023年式（年式も一緒に入力できます）</div>`);
      const res = await addInputWithChips(
        "車名を入力（例: アルファード）",
        "調べる",
        [],
        { inputLabel: "車名で入力", chipsLabel: "メーカーから選ぶ", groups: brandGroups(), collapseLocked: true }
      );
      if (res.type === "text") return runSearch(res.value, null);
      return brandModelStep(res.option.value);
    }

    /* 車名で見つからなかったときの導線。メーカー一覧だけを出し直す */
    async function brandPickStep() {
      addBot("<p>メーカーからお探しいただけます。お車のメーカーをお選びください。</p>");
      const res = await addInputWithChips(
        "車名を入力",
        "調べる",
        [],
        { chipsLabel: "メーカーから選ぶ", groups: brandGroups(), collapseLocked: true }
      );
      if (res.type === "text") return freshSearch(res.value);
      return brandModelStep(res.option.value);
    }

    /* メーカー選択チップを「国産」「輸入車」の2グループに分けて全件並べる */
    function brandGroups() {
      const pick = (tokens, pool) => tokens.map((t) => pool.find((b) => b.includes(t))).filter(Boolean);
      const jp = pick(JP_BRAND_ORDER, brandList);
      const imported = brandList.filter((b) => !jp.includes(b));
      const head = pick(IMPORT_BRAND_HEAD, imported);
      const tail = imported
        .filter((b) => !head.includes(b))
        .sort((a, b) => brandChipLabel(a).localeCompare(brandChipLabel(b), "ja"));
      const toOption = (b) => ({ label: brandChipLabel(b), value: b });
      return [
        { label: "国産", options: jp.map(toOption) },
        { label: "輸入車", options: head.concat(tail).map(toOption) },
      ];
    }

    function popularFor(brandStr, token) {
      const names = (token && POPULAR[token]) || [];
      const variants = [...new Set(
        carData.filter((e) => e.brand === brandStr).flatMap((e) => e.model.split("\n"))
      )];
      const normed = variants.map(norm);
      const hits = names.filter((n) => {
        const q = norm(n);
        return normed.some((v) => v === q || v.startsWith(q));
      });
      if (hits.length) return hits;
      return variants.slice(0, 8);
    }

    /* メーカー確定後の共通ステップ: 人気車種チップ＋メーカー絞り込みの車名入力 */
    async function brandModelStep(brandStr) {
      if (!brandStr) return start();
      const token = Object.keys(POPULAR).find((t) => brandStr.includes(t)) || null;
      const models = popularFor(brandStr, token);
      addBot(`<p><b>${esc(brandChipLabel(brandStr))}</b> のお車ですね。車名を入力するか、下の候補からお選びください。</p>`);
      const res = await addInputWithChips(
        "車名を入力",
        "調べる",
        models.map((m) => ({ label: m, value: m })).concat([{ label: "ほかのメーカーを選ぶ", value: null, ghost: true }]),
        { chipsLabel: "よく調べられている車種" }
      );
      /* ここで車名を入力し直した場合は選んだメーカーを引きずらない。
         別メーカーの車名を入れ直すケースが多いため、はじめからやり直したのと同じ扱いにする */
      if (res.type === "text") return freshSearch(res.value);
      if (res.option.value) return runSearch(res.option.value, brandStr);
      return start();
    }

    /* 会話ログを流して、まっさらな状態からこの車名で調べ直す */
    function freshSearch(query) {
      runId++;
      autoScroll = true;
      logEl.innerHTML = "";
      return runSearch(query, null);
    }

    /* 入力テキストがメーカー名かどうかを判定する。
       カッコ内の読み（メルセデスベンツ等）・ラテン表記（BMW等）・エイリアス（ベンツ等）で照合 */
    function detectBrands(query) {
      const q = norm(query);
      if (q.length < 2) return [];
      const aliasTarget = BRAND_TEXT_ALIASES[query.normalize("NFKC").trim()] || BRAND_TEXT_ALIASES[q];
      return brandList.filter((b) => {
        if (aliasTarget && b.includes(aliasTarget)) return true;
        return norm(b).includes(q);
      });
    }

    async function runSearch(rawQuery, brandScope) {
      addUser(rawQuery);
      const desiredYear = extractYear(rawQuery);
      const queryText = rawQuery.replace(/((?:19|20)\d{2})\s*年?\s*式?/, " ").trim() || rawQuery;

      let matches = matchModels(queryText);
      if (brandScope) {
        const scoped = matches.filter((m) => m.brand === brandScope);
        if (scoped.length) matches = scoped;
      }

      const brandHits = matches.length === 0 ? detectBrands(queryText) : [];
      const matchedBrands = [...new Set(matches.map((m) => m.brand))];
      const resolvedBrand = brandScope
        || (matchedBrands.length === 1 ? matchedBrands[0] : null)
        || (brandHits.length === 1 ? brandHits[0] : null);
      const params = { query: rawQuery, product: productKey };
      if (resolvedBrand) params.brand = brandShort(resolvedBrand);
      track("ai_fit_search", params);

      await addScan("お調べします。", [
        "入力内容を解析しています",
        `適合データベースを照合しています（${carData.length.toLocaleString()}車種）`,
        "候補を判定しています",
      ]);

      if (matches.length === 0) {
        /* 車種名でヒットしない場合、メーカー名の入力として解釈を試みる（例:「ベンツ」「トヨタ」） */
        if (brandHits.length === 1) {
          return brandModelStep(brandHits[0]);
        }
        if (brandHits.length > 1) {
          addBot("<p>メーカー名で見つかりました。どちらのメーカーですか？</p>");
          const picked = await addChips(
            brandHits.map((b) => ({ label: brandChipLabel(b), value: b }))
          );
          return brandModelStep(picked.value);
        }
        addBot(`<p>適合調査データに「${esc(queryText)}」は見つかりませんでした。呼び方によっては見つからないことがあるため、メーカー名からお探しいただくこともできます。未調査の車種の場合は、そのまま取り付けの見込みをご案内します。</p>`);
        const next = await addChips([
          { label: "メーカーから探す", value: "brand" },
          { label: "このまま取り付けを調べる", value: "fallback" },
        ]);
        if (next.value === "brand") return brandPickStep();
        return fallbackFlow({ label: rawQuery, year: desiredYear });
      }

      let chosen;
      if (matches.length === 1 && matches[0].score >= 82) {
        chosen = matches[0];
        addBot(`<p><b>${esc(brandShort(chosen.brand))} ${esc(chosen.model.split("\n")[0])}</b> が見つかりました。</p>`);
      } else {
        addBot(`<p>候補が ${Math.min(matches.length, 8)} 件見つかりました。お車はどちらですか？</p>`);
        const opts = matches.slice(0, 8).map((m) => ({
          label: m.model.split("\n").join(" / "),
          sub: brandShort(m.brand),
          value: m,
        }));
        opts.push({ label: "この中にない", value: null });
        const picked = await addChips(opts, { ghostLabels: ["この中にない"] });
        if (!picked.value) {
          addBot("<p>承知しました。適合調査データにないお車の可能性があります。</p>");
          return fallbackFlow({ label: rawQuery, year: desiredYear });
        }
        chosen = picked.value;
      }
      variantFlow(chosen, desiredYear);
    }

    async function variantFlow(chosen, desiredYear) {
      const entries = entriesFor(chosen.brand, chosen.model);
      const groups = variantGroups(entries);
      const carLabel = `${brandShort(chosen.brand)} ${chosen.model.split("\n")[0]}`;

      if (desiredYear) {
        const covered = coveredGroups(groups, desiredYear);
        if (covered.length === 0) return yearOutOfRange(groups, carLabel, desiredYear);
        if (covered.length === 1) {
          addBot(`<p>${desiredYear}年式は <b>年式 ${esc(covered[0].year)}（型式 ${esc(covered[0].type.split("\n").join("・"))}）</b> の調査データに該当します。</p>`);
          return featureFlow(covered[0]);
        }
        addBot(`<p>${desiredYear}年式に該当する調査データが複数あります。型式をお選びください。</p>`);
        const picked = await addChips(covered.map((g) => ({
          label: `年式 ${g.year}`,
          sub: `型式 ${g.type.split("\n").join("・")}`,
          value: g,
        })));
        return featureFlow(picked.value);
      }

      addBot(`<p><b>${esc(carLabel)}</b> の調査データが ${groups.length} 件あります。お車の年式・型式に近いものをお選びください。</p>`);
      const opts = groups.map((g) => ({
        label: `年式 ${g.year}`,
        sub: `型式 ${g.type.split("\n").join("・")}`,
        value: g,
      }));
      opts.push({ label: "年式がわからない・この中にない", value: null });
      const picked = await addChips(opts, { ghostLabels: ["年式がわからない・この中にない"] });

      if (picked.value) return featureFlow(picked.value);

      addBot("<p>お車の年式（初度登録年）を教えてください。西暦4桁で入力できます。</p>");
      const y = await askYear();
      if (!y) return fallbackFlow({ label: carLabel, year: null });
      return variantByYear(groups, carLabel, y);
    }

    async function askYear() {
      const yTxt = await addInput("例: 2023", "照合する");
      addUser(yTxt);
      const y = extractYear(yTxt) || parseInt(yTxt, 10);
      if (y && y >= 1980 && y <= 2100) return y;
      addBot("<p>年式を読み取れませんでした。西暦4桁（例: 2023）で入力してください。</p>");
      const y2Txt = await addInput("例: 2023", "照合する");
      addUser(y2Txt);
      const y2 = extractYear(y2Txt) || parseInt(y2Txt, 10);
      return y2 && y2 >= 1980 && y2 <= 2100 ? y2 : null;
    }

    async function variantByYear(groups, carLabel, year) {
      await addScan("年式を照合します。", ["調査データの年式範囲と照合しています"]);
      const covered = coveredGroups(groups, year);
      if (covered.length === 0) return yearOutOfRange(groups, carLabel, year);
      if (covered.length === 1) {
        addBot(`<p>${year}年式は <b>年式 ${esc(covered[0].year)}（型式 ${esc(covered[0].type.split("\n").join("・"))}）</b> の調査データに該当します。</p>`);
        return featureFlow(covered[0]);
      }
      addBot(`<p>${year}年式に該当する調査データが複数あります。型式をお選びください。</p>`);
      const picked = await addChips(covered.map((g) => ({
        label: `年式 ${g.year}`,
        sub: `型式 ${g.type.split("\n").join("・")}`,
        value: g,
      })));
      return featureFlow(picked.value);
    }

    async function yearOutOfRange(groups, carLabel, year) {
      const ranges = groups.map((g) => parseRange(g.year)).filter(Boolean);
      const newest = ranges.some((r) => r.e === Infinity) ? null : Math.max(...ranges.map((r) => Math.floor(r.e - 0.001)));
      const oldest = Math.min(...ranges.map((r) => Math.floor(r.s)));

      if (newest && year > newest) {
        /* 新型が調査範囲より新しいケース: 直前の世代を特定し、
           その世代で✓（取付可）を確認済みなら前向きな案内に切り替える */
        let prevGroup = null;
        let prevEnd = -Infinity;
        for (const g of groups) {
          const r = parseRange(g.year);
          if (!r || r.e === Infinity) continue;
          if (r.e > prevEnd) {
            prevEnd = r.e;
            prevGroup = g;
          }
        }
        const prevOk = prevGroup && prevGroup.entries.some((en) =>
          Object.values(en.compatibility_each_seat).some(
            (c) => product.seatDisplay(c, symbolsById, en).cls === "ok"
          )
        );
        if (prevOk) {
          const prev = { year: prevGroup.year, type: prevGroup.type.split("\n").join("・") };
          addBot(product.prevModel.message(carLabel, prev, year));
          return fallbackFlow({
            label: `${carLabel}（${year}年式）`,
            year,
            prevConfirmed: prev,
          });
        }
        addBot(`<p>${esc(carLabel)} の調査データは ${newest}年式ごろまでです。${year}年式はまだ適合調査が行われていない可能性があります。</p>`);
        return fallbackFlow({ label: `${carLabel}（${year}年式）`, year });
      }

      let msg;
      if (year < oldest) {
        msg = `${esc(carLabel)} の調査データは ${oldest}年式以降です。${year}年式は適合調査の対象外の可能性があります。`;
      } else {
        msg = `${year}年式に一致する調査データが見つかりませんでした。モデルチェンジ前後の可能性があります。`;
      }
      addBot(`<p>${msg}</p>`);
      return fallbackFlow({ label: `${carLabel}（${year}年式）`, year });
    }

    async function featureFlow(group) {
      const seen = new Map();
      for (const e of group.entries) {
        const key = e.feature + " " + e.capacity;
        if (!seen.has(key)) seen.set(key, e);
      }
      const opts = [...seen.values()];
      let entry;
      if (opts.length === 1) {
        entry = opts[0];
      } else {
        addBot("<p>グレード・仕様をお選びください。</p>");
        const picked = await addChips(opts.map((e) => ({
          label: e.feature.split("\n").join(" "),
          sub: `${e.capacity}人乗り`,
          value: e,
        })));
        entry = picked.value;
      }
      showDataResult(entry);
    }

    async function showDataResult(entry) {
      const carMeta = `${brandShort(entry.brand)} ${entry.model.split("\n")[0]} ${entry.type.split("\n").join("・")} ${entry.year}`;
      const capacity = parseInt(entry.capacity, 10);
      const hasThirdRow = capacity > 5;
      /* 表示する席（定員5人以下は後席の3席）にデータが1つもない車は、「取り付けできません」ではなく推定で案内する */
      const shownSeatIds = hasThirdRow ? [2, 3, 4, 5, 6, 7] : [2, 3, 4];
      if (product.fallbackWhenNoSeatData && !shownSeatIds.some((id) => entry.compatibility_each_seat[id])) {
        addBot("<p>このお車は、座席ごとの調査データがありません。取り付けの見込みをご案内します。</p>");
        return fallbackFlow({ label: carMeta, year: null });
      }

      await addScan("適合判定を行います。", [
        "座席ごとの適合記号を判定しています",
        "注意事項を確認しています",
      ]);

      const rows2 = [
        { id: 2, name: hasThirdRow ? "2列目右" : "後席右" },
        { id: 3, name: hasThirdRow ? "2列目中央" : "後席中央" },
        { id: 4, name: hasThirdRow ? "2列目左" : "後席左" },
      ];
      const rows3 = [
        { id: 5, name: "3列目右" },
        { id: 6, name: "3列目中央" },
        { id: 7, name: "3列目左" },
      ];

      let anyOk = false;
      const symbolIds = [];
      const seatHtml = (rows, heading) => {
        const lines = rows
          .filter((r) => entry.compatibility_each_seat[r.id])
          .map((r) => {
            const compat = entry.compatibility_each_seat[r.id];
            const d = product.seatDisplay(compat, symbolsById, entry);
            symbolIds.push(compat.front.compatibility_symbol_id);
            if (d.countsAsOk) anyOk = true;
            return `<div class="ai-fit-seat-line"><span class="ai-fit-seat-pos">${r.name}</span><span class="ai-fit-seat-mark ${d.cls}">${d.icon}</span><span class="ai-fit-seat-desc">${esc(d.description)}</span></div>`;
          });
        return lines.length ? `<div class="ai-fit-seat-heading">${heading}</div>${lines.join("")}` : "";
      };

      const seats2 = seatHtml(rows2, hasThirdRow ? "2列目シート" : "後席シート");
      const seats3 = hasThirdRow ? seatHtml(rows3, "3列目シート") : "";

      const v = product.verdict(symbolIds, anyOk);
      /* verdict はベリックスで「76cmから／年式しだい／ジュニアモードだけ」を見分けるため */
      track("ai_fit_result", { result: anyOk ? "data_ok" : "data_ng", verdict: v.cls, car: carMeta, product: productKey });

      const verdict = `<div class="ai-fit-verdict ${v.cls}"><span class="ai-fit-src">${product.dataSourceLabel}</span>${v.text}</div>`;

      const cta = anyOk
        ? `<div class="ai-fit-cta-row"><a class="ai-fit-cta buy" href="${product.buyUrl}">商品購入ページへ</a><a class="ai-fit-cta line" href="${LINE_URL}" target="_blank" rel="noreferrer noopener">LINEで相談する</a></div>`
        : `<div class="ai-fit-cta-row"><a class="ai-fit-cta line" href="${LINE_URL}" target="_blank" rel="noreferrer noopener">LINEで相談する</a></div>`;

      addBot(`
        ${verdict}
        <div class="ai-fit-vehicle-meta">${esc(brandShort(entry.brand))} ${esc(entry.model.split("\n").join(" / "))}｜型式 ${esc(entry.type.split("\n").join("・"))}｜年式 ${esc(entry.year)}｜${esc(entry.feature.split("\n").join(" "))}｜${esc(entry.capacity)}人乗り</div>
        <div class="ai-fit-seat-table">${seats2}${seats3}</div>
        ${anyOk ? `<p class="ai-fit-fine">${esc(product.resultNote)}</p>` : ""}
        ${cta}
      `);
      offerRestart();
    }

    /* ----- 推定フロー（製品別） ----- */
    function fallbackFlow(ctx) {
      if (product.fallback === "axion") return axionFallbackFlow(ctx);
      if (product.fallback === "velix") return velixFallbackFlow(ctx);
      return xenonFallbackFlow(ctx);
    }

    /* XENON 1: ISOFIX固定＋サポートレッグ専用。金具・座席形状・床下収納の3問。 */
    async function xenonFallbackFlow(ctx) {
      await addScan("ISOFIX推定モードに切り替えます。", [
        "調査データの範囲を確認しています",
        "ISOFIX取付条件のチェックリストを準備しています",
      ]);

      const xenonIntro = ctx.prevConfirmed
        ? "<p>新しいモデルでの取り付け条件を<b>3つの確認</b>でご案内します。</p>"
        : "<p>適合調査データがないお車でも、<b>3つの確認</b>で取り付けの見込みをご案内できます。</p>";
      addBot(`${xenonIntro}<p>まず、お車の後部座席に <b>ISOFIX固定金具</b> はありますか？</p><div class="ai-fit-hint">確認方法: 後部座席の座面と背もたれのすき間に「ISOFIX」のタグまたはマークがあります。${ctx.year && ctx.year >= 2013 ? "2012年7月以降に発売されたお車はISOFIXの装備が義務化されているため、通常は装備されています。" : ""}</div>`);

      let isofix = await addChips([
        { label: "ある", value: "yes" },
        { label: "ない", value: "no" },
        { label: "わからない", value: "unknown" },
      ]);

      const conditions = [];

      if (isofix.value === "unknown") {
        let year = ctx.year;
        if (!year) {
          addBot("<p>お車の年式（初度登録年）を教えてください。ISOFIXの装備時期を推定します。</p>");
          const yTxt = await addInput("例: 2018", "確認する");
          addUser(yTxt);
          year = extractYear(yTxt) || parseInt(yTxt, 10) || null;
        }
        if (year && year >= 2013) {
          addBot(`<p>2012年7月以降に発売された乗用車は、ISOFIX固定金具の装備が義務化されています。<b>${year}年式であれば装備されている可能性が高い</b>です。</p><div class="ai-fit-hint">念のため、後部座席の座面と背もたれのすき間の「ISOFIX」マークをご確認ください。</div>`);
        } else if (year && year >= 2007) {
          addBot(`<p>${year}年式は義務化（2012年7月）前ですが、この時期の車には装備されている例も多くあります。座面と背もたれのすき間の「ISOFIX」マークをご確認ください。</p>`);
        } else {
          addBot("<p>この年代のお車は装備の有無が車種ごとに異なります。座面と背もたれのすき間の「ISOFIX」マークをご確認ください。</p>");
        }
        isofix = await addChips([
          { label: "確認できた（ある）", value: "yes" },
          { label: "見当たらない（ない）", value: "no" },
          { label: "あとで現車で確認する", value: "later" },
        ]);
        if (isofix.value === "later") {
          conditions.push("ISOFIX固定金具の有無を現車でご確認ください（後席座面と背もたれのすき間の「ISOFIX」マーク）。");
        }
      }

      if (isofix.value === "no") {
        track("ai_fit_result", { result: "estimate_ng", car: ctx.label, product: productKey });
        addBot(`
          <div class="ai-fit-verdict ng"><span class="ai-fit-src">推定</span>取り付けできません</div>
          <p>XENON 1 は ISOFIX固定専用のチャイルドシートです。ISOFIX金具のないお車には、シートベルトでの取り付けはできません。</p>
          <div class="ai-fit-cta-row"><a class="ai-fit-cta line" href="${LINE_URL}" target="_blank" rel="noreferrer noopener">LINEで相談する</a></div>
        `);
        return offerRestart();
      }

      addBot("<p>取り付け予定の座席は、どちらのタイプですか？</p>");
      const seat = await addChips([
        { label: "ベンチシート", sub: "ひと続きの後部座席", value: "bench" },
        { label: "キャプテンシート", sub: "独立シート・肘掛け付き", value: "captain" },
      ]);
      if (seat.value === "captain") {
        conditions.push("キャプテンシートでは、肘掛けやセンターコンソールとの干渉により回転機能を十分にご使用いただけない場合があります。回転機能を活用されたい場合は、実店舗での取付テストか事前のお問い合わせをおすすめします。");
      }

      addBot("<p>最後に、取り付け予定の座席の足元に <b>床下収納（フロアボックス）</b> はありますか？</p><div class='ai-fit-hint'>XENON 1 はサポートレッグ（床につっぱる脚）で支える構造のため、脚の設置面が床下収納のフタの上になる場合は確認が必要です。</div>");
      const floor = await addChips([
        { label: "ない", value: "no" },
        { label: "ある", value: "yes" },
        { label: "わからない", value: "unknown" },
      ]);
      if (floor.value === "yes") {
        conditions.push("サポートレッグを床下収納のフタの上に設置できない場合があります。収納の位置と脚の設置面をご確認ください。");
      } else if (floor.value === "unknown") {
        conditions.push("サポートレッグの設置面に床下収納がないか、現車でご確認ください。");
      }

      await addScan("推定結果をまとめます。", ["取付条件を整理しています"]);
      renderEstimateResult(ctx, conditions, "ISOFIX金具のあるお車であれば、XENON 1（R129 i-Size認証）は規格上取り付けが可能です。");
    }

    /* AXION 1: ISOFIX固定に加え、3点式シートベルトのみでも設置可（製品仕様）。
       2点式（腰ベルトのみ）の座席は取付不可。 */
    async function axionFallbackFlow(ctx) {
      await addScan("取付条件の確認に切り替えます。", [
        "調査データの範囲を確認しています",
        "アクシオン1の取付方式（ISOFIX／3点式シートベルト）を確認しています",
      ]);

      const axionIntro = ctx.prevConfirmed
        ? "<p>アクシオン1は <b>ISOFIX固定</b> と <b>3点式シートベルト固定</b> の2つの取付方式に対応しています。新しいモデルでの取り付け条件を確認します。</p>"
        : "<p>適合調査データがないお車でも、アクシオン1は <b>ISOFIX固定</b> と <b>3点式シートベルト固定</b> の2つの取付方式に対応しているため、簡単な確認で見込みをご案内できます。</p>";
      addBot(`${axionIntro}<p>お車の後部座席に <b>ISOFIX固定金具</b> はありますか？</p><div class="ai-fit-hint">確認方法: 後部座席の座面と背もたれのすき間に「ISOFIX」のタグまたはマークがあります。${ctx.year && ctx.year >= 2013 ? "2012年7月以降に発売されたお車はISOFIXの装備が義務化されているため、通常は装備されています。" : ""}</div>`);

      const isofix = await addChips([
        { label: "ある", value: "yes" },
        { label: "ない", value: "no" },
        { label: "わからない", value: "unknown" },
      ]);

      const conditions = [];

      /* ISOFIX の有無にかかわらず、2点式ベルトかを必ず確かめる（取扱説明書: ISOFIX で付けるときも3点式ベルトが要る） */
      addBot("<p>取り付け予定の座席の <b>シートベルトは3点式</b>（肩ベルトあり）ですか？</p><div class='ai-fit-hint'>アクシオン1はISOFIXを使わなくても、3点式シートベルトで設置できます。腰ベルトのみの2点式座席には取り付けできません。</div>");
      const belt = await addChips([
        { label: "3点式（肩ベルトあり）", value: "three" },
        { label: "2点式（腰ベルトのみ）", value: "two" },
        { label: "わからない", value: "unknown" },
      ]);

      if (belt.value === "two") {
        track("ai_fit_result", { result: "estimate_ng", car: ctx.label, product: productKey });
        addBot(`
          <div class="ai-fit-verdict ng"><span class="ai-fit-src">推定</span>その座席には取り付けできません</div>
          <p>2点式（腰ベルトのみ）の座席にはアクシオン1を取り付けできません。3点式シートベルトのある別の座席をご検討ください。</p>
          <div class="ai-fit-cta-row"><a class="ai-fit-cta line" href="${LINE_URL}" target="_blank" rel="noreferrer noopener">LINEで相談する</a></div>
        `);
        return offerRestart();
      }

      if (belt.value === "unknown") {
        conditions.push("取り付け予定の座席のシートベルトが3点式（肩ベルトあり）か、現車でご確認ください。");
      }
      if (isofix.value === "unknown") {
        conditions.push("ISOFIXでの固定は、車種別の適合データで確認できた座席に限られます。ISOFIXを使いたい場合はLINEでご相談ください。");
      }

      await addScan("推定結果をまとめます。", ["取付条件を整理しています"]);
      /* 取扱説明書では、ISOFIX で使えるのは車種別適合表に載っている座席に限られる。
         データにない車では ISOFIX を約束せず、3点式シートベルトでの取り付けを案内する */
      renderEstimateResult(ctx, conditions, isofix.value === "yes"
        ? "アクシオン1は、お車の3点式シートベルトで取り付けできます。ISOFIXでの固定は、車種別の適合データで確認できた座席に限られます。このお車はデータにないため、ISOFIXを使いたい場合はLINEでご相談ください。"
        : "アクシオン1はISOFIXを使わなくても、お車の3点式シートベルトで設置できます。");
    }

    /* VELIX 1: ISOFIX は i-Size 対応座席だけ、3点式ベルトは i-Size 対応座席のないお車だけ
       （取扱説明書 第5面）。i-Size の表示 → 3点式ベルト の順に確かめる */
    async function velixFallbackFlow(ctx) {
      await addScan("取付条件の確認に切り替えます。", [
        "調査データの範囲を確認しています",
        "ベリックス1の取付方式（i-Size対応座席／3点式シートベルト）を確認しています",
      ]);

      const velixIntro = ctx.prevConfirmed
        ? "<p>新しいモデルでの取り付け条件を確認します。</p>"
        : "<p>適合調査データがないお車でも、簡単な確認で取り付けの見込みをご案内できます。</p>";
      addBot(`${velixIntro}<p>お車の取扱説明書（チャイルドシートの項目）や後部座席に、<b>i-Size</b> の表示はありますか？</p><div class="ai-fit-hint">ベリックス1の76〜105cm（5点式ハーネス）は、i-Size対応座席にISOFIXとトップテザーで取り付けます。i-Size対応座席かどうかは、座席のマークやお車の取扱説明書で確認できます。</div>`);

      const isize = await addChips([
        { label: "ある", value: "yes" },
        { label: "ない", value: "no" },
        { label: "わからない", value: "unknown" },
      ]);

      const conditions = [];

      if (isize.value === "yes") {
        await addScan("推定結果をまとめます。", ["取付条件を整理しています"]);
        return renderEstimateResult(ctx, conditions, "i-Size対応座席であれば、ベリックス1は76〜150cmのすべての使い方で取り付けできます（76〜105cm：ISOFIX＋トップテザー、100〜150cm：ISOFIX＋お車の3点式シートベルト）。トップテザー金具の位置は、お車の取扱説明書でご確認ください。");
      }

      addBot("<p>取り付け予定の座席の <b>シートベルトは3点式</b>（肩ベルトあり）ですか？</p><div class='ai-fit-hint'>i-Size対応座席がないお車では、100〜150cm（ジュニアモード）を、ISOFIXは使わずにお車の3点式シートベルトで取り付けます。腰ベルトのみの2点式座席には取り付けできません。</div>");
      const belt = await addChips([
        { label: "3点式（肩ベルトあり）", value: "three" },
        { label: "2点式（腰ベルトのみ）", value: "two" },
        { label: "わからない", value: "unknown" },
      ]);

      if (belt.value === "two") {
        track("ai_fit_result", { result: "estimate_ng", car: ctx.label, product: productKey });
        addBot(`
          <div class="ai-fit-verdict ng"><span class="ai-fit-src">推定</span>その座席には取り付けできません</div>
          <p>2点式（腰ベルトのみ）の座席にはベリックス1を取り付けできません。3点式シートベルトのある別の座席をご検討ください。</p>
          <div class="ai-fit-cta-row"><a class="ai-fit-cta line" href="${LINE_URL}" target="_blank" rel="noreferrer noopener">LINEで相談する</a></div>
        `);
        return offerRestart();
      }

      if (isize.value === "unknown") {
        conditions.push("お車にi-Size対応座席があるかを、お車の取扱説明書でご確認ください。i-Size対応座席があれば、その席にISOFIXで取り付け、76cmからすべての使い方でお使いいただけます。");
      }
      conditions.push("76〜105cm（5点式ハーネス）は使えないため、お子さまの身長が100cmになってから、ジュニアモードでお使いください。");
      conditions.push("取り付ける座席が、お車の取扱説明書で汎用（ユニバーサル）のチャイルドシートを付けられる座席かをご確認ください。");
      if (belt.value === "unknown") {
        conditions.push("取り付け予定の座席のシートベルトが3点式（肩ベルトあり）か、現車でご確認ください。");
      }

      await addScan("推定結果をまとめます。", ["取付条件を整理しています"]);
      renderEstimateResult(
        ctx,
        conditions,
        "i-Size対応座席がないお車では、ベリックス1の100〜150cm（ジュニアモード）を、ISOFIXは使わずにお車の3点式シートベルトで取り付けできます。",
        "100〜150cm（ジュニアモード）で取り付けできる見込みです"
      );
    }

    /* verdictText を渡すと推定の見出しを差し替える（ベリックスのジュニアモードだけの見込みなど） */
    function renderEstimateResult(ctx, conditions, leadText, verdictText) {
      const params = { result: conditions.length ? "estimate_cond" : "estimate_ok", car: ctx.label, product: productKey };
      if (ctx.prevConfirmed) params.prev_confirmed = true;
      track("ai_fit_result", params);

      if (ctx.prevConfirmed) {
        leadText = product.prevModel.lead(ctx.prevConfirmed.year) + leadText;
      }

      const condHtml = conditions.length
        ? `<ul class="ai-fit-cond-list">${conditions.map((c) => `<li><span class="ci">△</span><span>${esc(c)}</span></li>`).join("")}</ul>`
        : "";

      const verdictLabel = verdictText
        || (conditions.length ? "条件付きで取り付けできる見込みです" : "取り付けできる見込みが高いです");
      const verdict = `<div class="ai-fit-verdict est"><span class="ai-fit-src">推定</span>${esc(verdictLabel)}</div>`;

      addBot(`
        ${verdict}
        <p>${esc(leadText)}${conditions.length ? "以下の点をご確認ください。" : ""}</p>
        ${condHtml}
        <p class="ai-fit-fine">※ この判定は適合調査データに基づくものではなく、製品の取付方式とお答えいただいた条件からの推定です。ご不安な場合はLINEで車検証の情報をお送りください。個別にお調べします。</p>
        <div class="ai-fit-cta-row">
          <a class="ai-fit-cta buy" href="${product.buyUrl}">商品購入ページへ</a>
          <a class="ai-fit-cta line" href="${LINE_URL}" target="_blank" rel="noreferrer noopener">LINEで個別確認する</a>
        </div>
      `);
      offerRestart();
    }

    async function offerRestart() {
      await addChips([{ label: "別の車を調べる", value: "again" }], { ghostLabels: ["別の車を調べる"] });
      start();
    }

    /* ----- データ読み込み ----- */
    async function init() {
      const ds = container.dataset;
      try {
        let carJson;
        let symbolJson;
        let aliasJson;
        const embedded = window.AI_FIT_EMBEDDED;
        if (embedded && embedded.carFit && embedded.carFit[productKey]) {
          carJson = embedded.carFit[productKey];
          symbolJson = embedded.symbols;
          aliasJson = embedded.aliases || null;
        } else {
          [carJson, symbolJson, aliasJson] = await Promise.all([
            fetch(ds.carFitUrl).then((res) => res.json()),
            fetch(ds.symbolsUrl).then((res) => res.json()),
            ds.aliasesUrl
              ? fetch(ds.aliasesUrl).then((res) => res.json()).catch(() => null)
              : Promise.resolve(null),
          ]);
        }
        carData = carJson;
        brandList = [...new Set(carData.map((e) => e.brand))];
        symbolJson.symbols.forEach((s) => {
          symbolsById[s.id] = s;
        });
        if (aliasJson && aliasJson.aliases) {
          aliasPairs = Object.entries(aliasJson.aliases).map(([alias, exps]) => ({
            a: norm(alias),
            exps: exps.map(norm),
          }));
        }
        start();
      } catch (error) {
        console.error("適合データの読み込みに失敗しました:", error);
        logEl.innerHTML = '<div class="ai-fit-loading">適合データを読み込めませんでした。時間をおいて再度お試しください。</div>';
      }
    }

    init();
  }

  function boot() {
    document.querySelectorAll("[data-ai-fit]").forEach(createWidget);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
